import { useState, useEffect } from 'react';
import apiClient from '../services/axiosInterceptor';
import { useSchoolDetailsStore } from './useSchoolDetailsStore';

export interface TimetableTypeOption {
  label: string;
  value: string;
  id: string;
}

interface TimetableTypeResponse {
  id: string; // Mongo ObjectID hex string, not a number — GetTimetableTypes
              // (Go) returns models.TimetableType docs with a real ObjectID
  name: string;
  session: string;
}

interface TimetableTypesApiResponse {
  status: 'success' | 'error';
  data: TimetableTypeResponse[];
  message?: string;
}

const useTimetableTypes = (includePlaceholder: boolean = false) => {
  const [options, setOptions] = useState<TimetableTypeOption[]>([]);

  // GET /timetable-types supports an optional session_id filter
  // (GetTimetableTypes, adminControllers). Without it, this returns every
  // timetable type ever created for the school across all sessions —
  // scope it to the current one, same fix as ManageTimetableTypesModal /
  // TimeSlotsManager.
  const currentSessionId = useSchoolDetailsStore((state) => state.schoolDetails?.current_session?.id);

  useEffect(() => {
    // Nothing to scope the query to yet — wait rather than fetching
    // unscoped results.
    if (!currentSessionId) {
      setOptions(includePlaceholder ? [{ label: 'No timetable types available', value: '', id: '' }] : []);
      return;
    }

    const fetchTimetableTypes = async () => {
      try {
        const response = await apiClient.get<TimetableTypesApiResponse>('timetable-types', {
          params: { session_id: currentSessionId },
        });
        if (response.data.status === 'success') {
          if (response.data.data.length === 0) {
            if (includePlaceholder) {
              setOptions([{ label: 'No timetable types available', value: '', id: '' }]);
            } else {
              setOptions([]);
            }
          } else {
            const mapped = response.data.data
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((item) => ({
                label: item.name,
                value: item.name,
                id: item.id,
              }));
            if (includePlaceholder) {
              setOptions([
                { label: 'Select a Timetable Type', value: '', id: '' },
                ...mapped,
              ]);
            } else {
              setOptions(mapped);
            }
          }
        } else if (response.data.message === 'empty') {
          if (includePlaceholder) {
            setOptions([{ label: 'No timetable types available', value: '', id: '' }]);
          } else {
            setOptions([]);
          }
        }
      } catch (error) {
        console.error('Error fetching timetable types:', error);
        // Auth errors (401) are handled by your interceptor
      }
    };

    fetchTimetableTypes();
  }, [includePlaceholder, currentSessionId]);

  return options;
};

export default useTimetableTypes;